import { A } from "@solidjs/router";
import { Ban, Check, CrownIcon, Plus, RefreshCcw, Settings, X } from "lucide-solid";
import {
	type Component,
	type ComponentProps,
	createMemo,
	type JSXElement,
	Match,
	type ParentComponent,
	Show,
	Switch,
	splitProps
} from "solid-js";
import { tv } from "tailwind-variants";
import BookIcon from "~/Icons/Icon_Book-x.svg?component-solid";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import {
	Button,
	calculatePercentage,
	getUserCreditsBalance,
	getUserCreditsTotal,
	getUserPlanName,
	isUserCanUseCredits,
	isUserFree,
	isUserFromAppSumo,
	isUserFrozen,
	ProgressBar
} from "~/solidJs/shared";
import type { IUser } from "~/solidJs/shared/helpers/types";
import {
	constructPricingLink,
	createBuyCreditsProps,
	createPricingProps,
	openPricingPage,
	SubscriptionType
} from "~/solidJs/shared/models/pageParams/pricingPageParams";
import { openPricingUnsubscribePage } from "~/solidJs/shared/models/pageParams/unsubscribePagePrams";
import { Divider } from "~/solidJs/shared/ui/atoms/Divider/Divider";
import SnowFlakeIcon from "~/solidJs/shared/ui/atoms/Icons/10_Icon_SnowFlake.svg?component-solid";
import ClockIcon from "~/solidJs/shared/ui/atoms/Icons/16_Icon_Clock.svg?component-solid";
import CreditsIcon from "~/solidJs/shared/ui/atoms/Icons/16_Icon_Credits.svg?component-solid";
import { BuyCreditsEnterpriseMethods } from "~/solidJs/widgets/BuyCreditsEnterprise";
import { TaskLimitsSection } from "./UserSubscriptionWidget";

export enum PlanStatus {
	Active = "Active",
	Canceled = "Canceled",
	Inactive = "Inactive",
	Frozen = "Frozen",
	Due = "Due",
	Pending = "Pending",
	Legacy = "Legacy",
	NewTariff = "NewTariff"
}

type SubscriptionInfoWidgetProps = {
	planName?: string;
	planStatus?: PlanStatus;
	creditsLeft?: number;
	totalCredits?: number;
	progressPercentage?: number;
	refreshText?: string;

	ManageSubscriptionFeature?: JSXElement;
	UpgradeFeatures?: JSXElement;
	shouldRenderNodesLimits?: boolean;
};

const statusBadgeStyles = tv(
	{
		base: "caption-11-medium inline-flex items-center gap-0.5 rounded-full px-2 py-1.5 leading-[10px]",
		defaultVariants: {
			status: "Active"
		},
		variants: {
			status: {
				Active: "bg-status-positive text-status-accent-positive",
				Canceled: "bg-[#FEF0F3] text-status-accent-error",
				Inactive: "bg-[#FEF0F3] text-status-accent-error",
				Frozen: "bg-[#D7E1F5] text-[#336DFF]",
				Due: "bg-[#FEF0C7] text-[#F79009]",
				Pending: "bg-[#FEEAD7] text-background-orange",
				Legacy: "bg-background-gray-light text-text-secondary-v2",
				NewTariff: "bg-background-gray-light text-text-secondary-v2"
			}
		}
	},
	{ twMerge: true }
);

const StatusIcon: Component<{ status: PlanStatus }> = (props) => {
	switch (props.status) {
		case PlanStatus.Active:
			return <Check size={16} />;
		case PlanStatus.Canceled:
		case PlanStatus.Inactive:
			return <X size={16} />;
		case PlanStatus.Frozen:
			return <SnowFlakeIcon class="size-4" />;
		case PlanStatus.Due:
		case PlanStatus.Pending:
			return <ClockIcon class="size-4" />;
		case PlanStatus.Legacy:
			return <BookIcon />;
		case PlanStatus.NewTariff:
			return <ClockIcon />;
		default:
			return null;
	}
};

type StatusBadgeCustomProps = {
	status: PlanStatus;
	children?: JSXElement;
	class?: string;
	className?: string;
};

export type StatusBadgeProps = StatusBadgeCustomProps &
	Omit<ComponentProps<"div">, keyof StatusBadgeCustomProps>;

export const StatusBadge: Component<StatusBadgeProps> = (props) => {
	const [local, others] = splitProps(props, ["status", "children", "class", "className"]);

	const getStatusText = () => {
		if (local.children) return local.children;

		switch (local.status) {
			case PlanStatus.Active:
				return "Active";
			case PlanStatus.Canceled:
				return "Canceled";
			case PlanStatus.Inactive:
				return "Inactive";
			case PlanStatus.Frozen:
				return "Frozen";
			case PlanStatus.Due:
				return "Due";
			case PlanStatus.Pending:
				return "Pending";
			case PlanStatus.Legacy:
				return "Legacy";
			default:
				return "Inactive";
		}
	};

	return (
		<div
			class={statusBadgeStyles({
				status: local.status,
				class: local.class || local.className
			})}
			{...others}
		>
			<StatusIcon status={local.status || PlanStatus.Active} />
			<span>{getStatusText()}</span>
		</div>
	);
};

export interface SubscriptionInfoEntityProps {
	planName?: string;
	badges?: JSXElement;
	manageSubscriptionAction?: JSXElement;
	content?: JSXElement;
	bottomSection?: JSXElement;
	infoMessage?: string;
	hideHeaderDivider?: boolean;
}

const InfoMessageBanner: Component<{ message: string }> = (props) => {
	return (
		<div class="flex items-center gap-2.5 rounded-lg bg-background-gray-light px-4 py-3">
			<div class="flex size-5 shrink-0 items-center justify-center rounded-full bg-text-secondary">
				<span class="font-medium text-[10px] text-white leading-none">i</span>
			</div>
			<span class="caption-13-medium text-text-secondary">{props.message}</span>
		</div>
	);
};

export const SubscriptionInfoEntity: Component<SubscriptionInfoEntityProps> = (props) => {
	return (
		<div class="flex w-full flex-col gap-8 rounded-4xl bg-white p-10">
			{/* Header section - no gap needed here, internal gap-5 is enough */}
			<div class="flex flex-col gap-5">
				<div class="flex w-full items-center justify-between">
					<div class="flex h-8 items-center gap-2">
						<Show when={props.planName}>
							<div class="header-24-semibold text-text-primary">{props.planName}</div>
						</Show>
						{props.badges}
					</div>
					{props.manageSubscriptionAction}
				</div>
				<Show when={!props.hideHeaderDivider}>
					<Divider />
				</Show>
			</div>
			{/* Info message, content, and bottom section with gap-8 between them */}
			<Show when={props.infoMessage}>
				<InfoMessageBanner message={props.infoMessage!} />
			</Show>
			{props.content}
			{props.bottomSection}
		</div>
	);
};

export const UpgradeSubscriptionEnterpriseFeature: Component<{
	shouldShowUpgradeButton?: boolean;
	shouldShowMoreCreditsButton?: boolean;
	source?: string;
	isUserFromAppSumo?: boolean;
}> = (props) => {
	const source = props.source || "team_subscription";

	const handleBuyMoreCredits = () => {
		BuyCreditsEnterpriseMethods.openBuyCreditsEnterprise();
		EventAnalytics.creditsRequested("team", source);
		if (source === "team_subscription") {
			EventAnalytics.team.subscription.getCreditsClick();
		}
	};

	const handleUpgradeClick = () => {
		EventAnalytics.subscriptionUiInteracted("upgrade", "team", source);
		if (source === "team_subscription") {
			EventAnalytics.team.subscription.upgradeClick();
		}
	};

	return (
		<div class="flex items-center gap-2">
			<Show when={props.shouldShowMoreCreditsButton}>
				<Button
					component="button"
					onClick={handleBuyMoreCredits}
					variant="SecondaryOverlay"
					size="large"
				>
					<Plus size={16} class="text-interaction-violet" />
					<span class="typography-text-14-medium text-interaction-violet">
						Get more credits
					</span>
				</Button>
			</Show>

			<Show when={props.shouldShowUpgradeButton}>
				<Switch
					fallback={
						<Button
							component={"a"}
							target="_blank"
							variant="actionviolent"
							size="large"
							class="gap-2.5"
							href={constructPricingLink({
								source: "account",
								type: SubscriptionType.Enterprise
							})}
							onClick={handleUpgradeClick}
						>
							<CrownIcon class="h-4 w-4 text-white" />
							<span class="typography-text-14-medium text-white">Upgrade</span>
						</Button>
					}
				>
					<Match when={props.isUserFromAppSumo}>
						<Button
							component={"a"}
							target="_blank"
							variant="actionviolent"
							size="large"
							class="gap-2.5"
							href="https://appsumo.com/account/products "
							onClick={handleUpgradeClick}
						>
							<CrownIcon class="h-4 w-4 text-white" />
							<span class="typography-text-14-medium text-white">Upgrade</span>
						</Button>
					</Match>
				</Switch>
			</Show>
		</div>
	);
};

const LeftCreditsDisplay: Component<{
	creditsLeft?: number;
	displayTeamCredits?: boolean;
	isUserInfinity?: boolean;
}> = (props) => {
	return (
		<div class="flex flex-col gap-2">
			<div class="typography-text-14-medium text-text-tertiary">Left:</div>
			<div class="flex items-end gap-1">
				<div class="flex items-center gap-1">
					{/* Credits Icon */}
					<CreditsIcon class="size-8 text-text-primary" />
					<Switch>
						<Match when={props.displayTeamCredits}>
							<span class="font-semibold text-[44px] text-text-primary leading-[34px]">
								{props.creditsLeft || 0}
							</span>
						</Match>
						<Match when={!props.isUserInfinity}>
							<span class="font-semibold text-[44px] text-text-primary leading-[34px]">
								{props.creditsLeft || 0}
							</span>
						</Match>
						<Match when={props.isUserInfinity}>
							<span class="font-semibold text-[44px] text-text-primary leading-[34px]">
								Unlimited
							</span>
						</Match>
					</Switch>
				</div>
				<div class="flex items-center justify-center pb-0.5">
					<span class="typography-text-14-medium text-text-primary">credits</span>
				</div>
			</div>
		</div>
	);
};

const TotalCreditsDisplay: Component<{
	totalCredits?: number | string;
	displayTeamCredits?: boolean;
	isUserInfinity?: boolean;
}> = (props) => {
	return (
		<div class="flex flex-col items-end gap-5">
			<Show when={props.displayTeamCredits ? true : !props.isUserInfinity}>
				<div class="typography-text-14-medium text-right text-text-tertiary">Total:</div>
				<div class="flex items-end gap-1">
					{/* Smaller Credits Icon */}
					<CreditsIcon class="size-5 text-text-secondary" />
					<span class="header-20-semibold text-right text-text-secondary">
						{props.totalCredits ?? "1 500"}
					</span>
					<div class="flex items-center justify-center pb-0.5">
						<span class="typography-text-14-medium text-text-secondary">credits</span>
					</div>
				</div>
			</Show>
		</div>
	);
};

export const RefreshDescription: ParentComponent = (props) => {
	return <div class="caption-12-medium text-text-secondary">{props.children}</div>;
};

const CreditsProgressSection: Component<{ progressPercentage?: number; refreshText?: string }> = (
	props
) => {
	return (
		<div class="flex flex-col gap-2">
			<ProgressBar percentage={props.progressPercentage || 0} />
			<Show when={props.refreshText}>
				<RefreshDescription>{props.refreshText}</RefreshDescription>
			</Show>
		</div>
	);
};

export const CreditsSection: Component<{
	creditsLeft?: number;
	totalCredits?: number | string;
	progressPercentage?: number;
	refreshText?: string;
	UpgradeFeatures?: JSXElement;
	displayTeamCredits?: boolean;
	isUserInfinity?: boolean;
}> = (props) => {
	return (
		<div class="flex flex-col gap-6">
			{/* Credits Info */}
			<div class="flex flex-col gap-3">
				{/* Credits Display */}
				<div class="flex w-full items-center justify-between">
					<LeftCreditsDisplay
						displayTeamCredits={props.displayTeamCredits}
						creditsLeft={props.creditsLeft}
						isUserInfinity={props.isUserInfinity}
					/>
					<TotalCreditsDisplay
						displayTeamCredits={props.displayTeamCredits}
						totalCredits={props.totalCredits}
						isUserInfinity={props.isUserInfinity}
					/>
				</div>

				{/* Progress bar + Description */}
				<CreditsProgressSection
					progressPercentage={props.progressPercentage}
					refreshText={props.refreshText}
				/>
			</div>

			{props.UpgradeFeatures}
		</div>
	);
};

interface UpgradeSubscriptionUserFeatureProps {
	shouldShowUpgradeButton?: boolean;
	shouldShowMoreCreditsButton?: boolean;
	source?: string;
	disabled?: boolean;
}

const UpgradeSubscriptionUserFeature: Component<UpgradeSubscriptionUserFeatureProps> = (props) => {
	return (
		<div class="flex items-center gap-2">
			<Show when={props.shouldShowMoreCreditsButton}>
				<Button
					component={A}
					{...createBuyCreditsProps(props.source || "get_more_credits")}
					variant="SecondaryOverlay"
					size="large"
				>
					<Plus size={16} class="text-interaction-violet" />
					<span class="typography-text-14-medium text-interaction-violet">
						Get more credits
					</span>
				</Button>
			</Show>

			<Show when={true || props.shouldShowUpgradeButton}>
				<Switch
					fallback={
						<Button
							component={"a"}
							target="_blank"
							variant="actionviolent"
							size="large"
							class="gap-2.5"
							disabled={props.disabled}
							{...createPricingProps("account")}
						>
							<CrownIcon class="h-4 w-4 text-white" />
							<span class="typography-text-14-medium text-white">Upgrade</span>
						</Button>
					}
				>
					<Match when={isUserFromAppSumo(getUserStore())}>
						<Button
							component={"a"}
							target="_blank"
							variant="actionviolent"
							size="large"
							class="gap-2.5"
							disabled={props.disabled}
							href="https://appsumo.com/account/products "
						>
							<CrownIcon class="h-4 w-4 text-white" />
							<span class="typography-text-14-medium text-white">Upgrade</span>
						</Button>
					</Match>
				</Switch>
			</Show>
		</div>
	);
};

const getPlanStatusAccordUserStatus = (user: IUser | null): PlanStatus => {
	if (!user) return PlanStatus.Inactive;
	switch (user.subscription_status) {
		case "active":
			return PlanStatus.Active;
		case "inactive":
			return PlanStatus.Inactive;
		case "frozen":
			return PlanStatus.Frozen;
		case "due":
			return PlanStatus.Due;
		default:
			return PlanStatus.Inactive;
	}
};

const prepearePropsSubscriptionEntity = (): Partial<SubscriptionInfoWidgetProps> => {
	const userStore = createMemo(() => getUserStore());
	return {
		planName: getUserPlanName(userStore()),
		planStatus: getPlanStatusAccordUserStatus(userStore()),
		creditsLeft: getUserCreditsBalance(userStore()),
		totalCredits: getUserCreditsTotal(userStore()),
		progressPercentage: calculatePercentage(
			0,
			getUserCreditsTotal(userStore()),
			getUserCreditsBalance(userStore())
		),
		refreshText: "" // Simplified for now
	};
};

const ManageUserSubscriptionAndCreditsFeature = () => {
	return (
		<div class="flex gap-1">
			<Switch>
				<Match when={isUserFromAppSumo(getUserStore())}>
					<Button
						component="a"
						href="https://appsumo.com/account/products"
						target="_blank"
						variant="TertiaryOverlay"
					>
						<Ban class="h-4 w-4" />
						<span class="caption-12-medium">Cancel plan</span>
					</Button>
					<Button
						component="a"
						href="https://appsumo.com/account/products"
						target="_blank"
						variant="TertiaryOverlay"
					>
						<RefreshCcw class="h-4 w-4" />
						<span class="caption-12-medium">Change plan</span>
					</Button>
				</Match>
				<Match when={isUserFree(getUserStore())}>
					<Button
						variant="TertiaryOverlay"
						onClick={() => {
							EventAnalytics.subscriptionUiInteracted("manage_subscription");
							openPricingPage({
								source: "namage-subscription-free"
							});
						}}
					>
						<Settings class="h-4 w-4" />
						<span class="caption-12-medium">Manage subscription</span>
					</Button>
				</Match>
				<Match when={!isUserFree(getUserStore())}>
					<Button
						variant="TertiaryOverlay"
						onClick={() => {
							openPricingUnsubscribePage();
							EventAnalytics.subscriptionUiInteracted("cancel_plan");
						}}
					>
						<Ban class="h-4 w-4" />
						<span class="caption-12-medium">Cancel plan</span>
					</Button>
					<Button
						variant="TertiaryOverlay"
						onClick={() => {
							EventAnalytics.subscriptionUiInteracted("change_plan");
							openPricingPage({
								source: "account-cancel-plan"
							});
						}}
						disabled={isUserFrozen(getUserStore())}
					>
						<RefreshCcw class="h-4 w-4" />
						<span class="caption-12-medium">Change plan</span>
					</Button>
				</Match>
			</Switch>
		</div>
	);
};

// Legacy SubscriptionInfoEntity for Subscription & Credits page (from master)
const SubscriptionInfoEntityOld: Component<SubscriptionInfoWidgetProps> = (props) => {
	const userStore = createMemo(() => getUserStore());

	return (
		<div class="flex w-full flex-col gap-8 rounded-4xl bg-white p-10">
			{/* Plan info + divider */}
			<div class="flex flex-col gap-5">
				{/* Plan + Buttons */}
				<div class="flex w-full items-center justify-between">
					{/* Plan + Status */}
					<div class="flex h-8 items-center gap-2">
						<div class="header-24-semibold text-text-primary">
							{props.planName || "Plan"}
						</div>
						<Show when={props.planStatus}>
							<StatusBadge status={props.planStatus!}>{props.planStatus}</StatusBadge>
						</Show>
					</div>
					{props.ManageSubscriptionFeature}
				</div>

				<Divider />
			</div>
			<Switch>
				<Match when={userStore()?.plan_status === "old"}>
					<TaskLimitsSection
						shouldRenderNodesLimits={props.shouldRenderNodesLimits}
						UpgradeFeatures={props.UpgradeFeatures}
						refreshText={props.refreshText}
					/>
				</Match>
				<Match when={userStore()?.plan_status !== "old"}>
					{/* Credits Section for Regular Users */}
					<CreditsSection
						creditsLeft={props.creditsLeft}
						totalCredits={props.totalCredits}
						progressPercentage={props.progressPercentage}
						refreshText={props.refreshText}
						UpgradeFeatures={props.UpgradeFeatures}
						isUserInfinity={false}
					/>
				</Match>
			</Switch>
		</div>
	);
};

export const SubscriptionAndCredintsInfoWidget: Component = () => {
	const userStore = createMemo(() => getUserStore());
	return (
		<SubscriptionInfoEntityOld
			{...prepearePropsSubscriptionEntity()}
			ManageSubscriptionFeature={<ManageUserSubscriptionAndCreditsFeature />}
			shouldRenderNodesLimits={true}
			UpgradeFeatures={
				<UpgradeSubscriptionUserFeature
					source="personal_subscription"
					shouldShowMoreCreditsButton={isUserCanUseCredits(userStore())}
					shouldShowUpgradeButton
					disabled={isUserFrozen(userStore())}
				/>
			}
		/>
	);
};
